import type { Flight, FlightStatus } from './index.d';
import { FLIGHT_STATUS } from './constants';

// Cached flight overrides parsed from the URL — undefined means not yet resolved.
let cachedFlightOverrides: string[] | undefined;

/** Exposed for testing only — resets the module-level URL override cache. */
export const clearFlightOverrideCache = (): void => {
  cachedFlightOverrides = undefined;
};

/** Exposed for testing only — pre-populates the override cache to simulate a ?flights= URL param. */
export const setFlightOverrideCache = (overrides: string[]): void => {
  cachedFlightOverrides = overrides;
};

/**
 * Returns a FlightStatus override based on the `?flights=` URL parameter in non-prod environments.
 * Accepts a comma-separated list of flight IDs or names (e.g. ?flights=123,myFlightName).
 * The URL is parsed only once per page load; subsequent calls use the cached value.
 * Returns IN_TREATMENT if any value matches, NOT_IN_FLIGHT if the param is present but no match,
 * or null if no override is active (fall through to normal logic).
 */
export const getFlightUrlOverrideStatus = (
  treatmentFlightId?: number,
  treatmentFlightName?: string
): FlightStatus | null => {
  if (cachedFlightOverrides === undefined) {
    cachedFlightOverrides = (new URLSearchParams(window.location.search).get('flights') ?? '').split(',').map(s => s.trim()).filter(Boolean);
  }

  if (cachedFlightOverrides.length === 0) {
    return null;
  }

  const idMatch = treatmentFlightId != null && cachedFlightOverrides.includes(String(treatmentFlightId));
  const nameMatch = !!treatmentFlightName && cachedFlightOverrides.includes(treatmentFlightName);
  return (idMatch || nameMatch) ? FLIGHT_STATUS.IN_TREATMENT : FLIGHT_STATUS.NOT_IN_FLIGHT;
};

/**
 * Checks if a flight matches the given flight ID or flight name.
 * ID matching takes precedence over name matching.
 *
 * @param flight - The flight object to check
 * @param flightId - Optional flight ID to match against
 * @param flightName - Optional flight name to match against
 * @returns true if the flight matches by ID or name, false otherwise
 */
export const isFlightMatch = (
  flight: Flight | undefined,
  flightId?: number,
  flightName?: string
): boolean => {
  if (!flight) {
    return false;
  }

  return (flightId !== undefined && flight.FlightId === flightId) ||
    (flightName !== undefined && flight.FlightName === flightName);
};

/**
 * Returns true when the given hostname is not a known production/int Ads host.
 * Local/dev/test hostnames (including jsdom's "localhost") fall through as local.
 */
export const isLocalHostname = (hostname: string): boolean => {
  const lower = hostname.toLowerCase();
  return lower === 'app1.localhost.ccui.devdev.bingads.microsoft.com' ||
    !/(^|\.)(bing)?ads(-int)?\.microsoft\.com$/.test(lower);
};
